import React, { useCallback, useContext, useMemo, useState } from 'react';
import Button from '../components/button';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import { useIntl } from 'react-intl';
import translations from './rolleliste.i18n';
import { useFormatMessageFor } from '../i18n/translation';
import {
  BrukerrollerDto,
  HttpError,
  MuligePraksisstedDto,
  NyPraksisstedbrukerRolleDto,
  NyStudiestedbrukerRolleDto,
  PraksisstedbrukerDto,
  RolleDto,
  StudiestedbrukerDto,
  StudiestedRollerDto,
} from '../api/generated/studiested-generated-api';
import { PraksisstedRollerDto } from '../api/generated/praksissted-generated-api';
import { NavLink } from 'react-router-dom';
import Spinner from '../components/spinner/spinner';
import { UhaUserContext } from '../user/UhaUserStore';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import RollelisteStudiestedbrukerDialog from './rollelistecomponents/rollelisteStudiestedbrukerDialog';
import RollelistePraksisstedbrukerDialog from './rollelistecomponents/rollelistePraksisstedbrukerDialog';
import { useActivityContextUHA } from './activitycontexts';

enum Header {
  NAVN,
  ROLLE,
  EPOST,
  MOBIL,
  HANDLING,
}

const RolleListe: React.FC<
  React.PropsWithChildren<{
    sted: string;
    emnekode: string;
    emnenavn: string;
  }>
> = ({ sted, emnekode, emnenavn }) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { praksisaktivitet } = useActivityContextUHA();
  const { id: aktivitetsId } = praksisaktivitet;

  const {
    data: rollerData,
    loading: rollerLoading,
    reload: rollerReload,
  } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdRolleGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
  );

  const { uhaUser: bruker } = useContext(UhaUserContext);

  const { addErrorNotification, addSuccessNotification } = useContext(
    NotificationFunctionContext,
  );

  const [activeUhaCol, setActiveUhaCol] = useState<number>(-1);
  const [activePsaCol, setActivePsaCol] = useState<number>(-1);
  const [uhaTableRows, setUhaTableRows] = useState<JSX.Element[]>();
  const [psaTableRows, setPsaTableRows] = useState<JSX.Element[]>();

  const [selectedPraksissteder, setSelectedPraksissteder] =
    useState<MuligePraksisstedDto>();

  const { locale } = useIntl();
  const labels = useFormatMessageFor(translations);

  const getHeaders = useCallback(
    (studiested: boolean) => [
      {
        title: studiested ? labels.uhaHeader1 : labels.psaHeader1,
        type: Header.NAVN,
      },
      { title: labels.header2, type: Header.ROLLE },
      { title: labels.header3, type: Header.EPOST },
      { title: labels.header4, type: Header.MOBIL },
      { title: labels.header5, type: Header.HANDLING },
    ],
    [labels],
  );

  const headersUha = useMemo(() => getHeaders(true), [getHeaders]);

  const headersPsa = useMemo(() => getHeaders(false), [getHeaders]);

  const handleAddSuccessStudiestedLabel = (
    rolleTypeStudiested: StudiestedRollerDto,
  ) => {
    switch (rolleTypeStudiested) {
      case StudiestedRollerDto.Koordinator:
        return labels.addSuccessKoordinator;
      case StudiestedRollerDto.Underviser:
        return labels.addSuccessUnderviser;
      default:
        return '';
    }
  };

  const handleRemoveSuccessStudiestedLabel = useCallback(
    (rolleType: StudiestedRollerDto) => {
      switch (rolleType) {
        case StudiestedRollerDto.Koordinator:
          return labels.removeSuccessKoordinator;
        case StudiestedRollerDto.Underviser:
          return labels.removeSuccessUnderviser;
        default:
          return '';
      }
    },
    [labels.removeSuccessKoordinator, labels.removeSuccessUnderviser],
  );

  const handleDeleteStudiestedbruker = useCallback(
    (brukerId: string, rolleType: StudiestedRollerDto | undefined) => {
      if (rolleType) {
        praksiskalenderApi
          .praksisaktivitetIdStudiestedrolleDelete({
            pathParams: { id: aktivitetsId },
            queryParams: {
              studiestedbrukerId: brukerId,
              type: rolleType.toString(),
            },
          })
          .then(() => {
            addSuccessNotification(
              handleRemoveSuccessStudiestedLabel(rolleType),
            );
            rollerReload();
          })
          .catch((err: HttpError) => {
            addErrorNotification(err.body);
          });
      }
    },
    [
      addSuccessNotification,
      addErrorNotification,
      aktivitetsId,
      handleRemoveSuccessStudiestedLabel,
      praksiskalenderApi,
      rollerReload,
    ],
  );

  const handleDeletePraksisstedbruker = useCallback(
    (brukerId: string) => {
      praksiskalenderApi
        .praksisaktivitetIdPraksisstedrolleDelete({
          pathParams: { id: aktivitetsId },
          queryParams: {
            praksisstedbrukerId: brukerId,
          },
        })
        .then(() => {
          addSuccessNotification(labels.removeSuccessVeileder);
          rollerReload();
        })
        .catch((err: HttpError) => {
          addErrorNotification(err.body);
        });
    },
    [
      addSuccessNotification,
      addErrorNotification,
      aktivitetsId,
      labels.removeSuccessVeileder,
      praksiskalenderApi,
      rollerReload,
    ],
  );

  const canWrite = useMemo(
    () => bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet,
    [bruker],
  );

  const studiestedbrukerTableRow = useCallback(
    (rolle: RolleDto) => (
      <TableRow
        key={`${rolle.type.studiestedrolle}_${rolle.etternavn}_${rolle.fornavn}_${rolle.epost}`}
      >
        <TableCellTd header={headersUha[Header.NAVN].title}>
          <NavLink to={`/myinstitution/studiestedbrukerprofil/${rolle.id}`}>
            {rolle.fornavn + ' ' + rolle.etternavn}
          </NavLink>
        </TableCellTd>
        <TableCellTd header={headersUha[Header.ROLLE].title}>
          {rolle.type.studiestedrolle}
        </TableCellTd>
        <TableCellTd header={headersUha[Header.EPOST].title}>
          {rolle.epost}
        </TableCellTd>
        <TableCellTd header={headersUha[Header.MOBIL].title}>
          {rolle.telefonnummer}
        </TableCellTd>
        <TableCellTd header={headersUha[Header.HANDLING].title} align={'right'}>
          {canWrite && (
            <Button.Tertiary
              id={'removeUser_' + rolle.id}
              onClick={() => {
                handleDeleteStudiestedbruker(
                  rolle.id,
                  rolle.type.studiestedrolle,
                );
              }}
            >
              {labels.removeUser}
            </Button.Tertiary>
          )}
        </TableCellTd>
      </TableRow>
    ),
    [canWrite, handleDeleteStudiestedbruker, headersUha, labels.removeUser],
  );

  const praksisstedbrukerTableRow = useCallback(
    (rolle: RolleDto) => {
      const navn = rolle.fornavn + ' ' + rolle.etternavn;

      return (
        <TableRow
          key={`${rolle.type.praksisstedrolle}_${rolle.etternavn}_${rolle.fornavn}_${rolle.epost}`}
        >
          <TableCellTd header={headersPsa[Header.NAVN].title}>
            {rolle.slettet ? (
              <>{navn}</>
            ) : (
              <NavLink
                to={`/practiceplaces/praksisstedbrukerprofil/${rolle.id}`}
              >
                {navn}
              </NavLink>
            )}
          </TableCellTd>
          <TableCellTd header={headersPsa[Header.ROLLE].title}>
            {rolle.type.praksisstedrolle}
          </TableCellTd>
          <TableCellTd header={headersPsa[Header.EPOST].title}>
            {rolle.epost}
          </TableCellTd>
          <TableCellTd header={headersPsa[Header.MOBIL].title}>
            {rolle.telefonnummer}
          </TableCellTd>
          <TableCellTd
            header={headersPsa[Header.HANDLING].title}
            align={'right'}
          >
            {canWrite &&
              rolle.type.praksisstedrolle &&
              rolle.type.praksisstedrolle.toString() ===
                PraksisstedRollerDto.Veileder && (
                <Button.Tertiary
                  id={'removePsUser_' + rolle.id}
                  onClick={() => {
                    handleDeletePraksisstedbruker(rolle.id);
                  }}
                >
                  {labels.removeUser}
                </Button.Tertiary>
              )}
          </TableCellTd>
        </TableRow>
      );
    },
    [canWrite, handleDeletePraksisstedbruker, headersPsa, labels.removeUser],
  );

  const saveStudiestedbrukerModal = (
    studiestedbruker: StudiestedbrukerDto,
    rolleTypeStudiested: StudiestedRollerDto,
  ) => {
    const rolle: NyStudiestedbrukerRolleDto = {
      studiestedbrukerId: studiestedbruker && studiestedbruker.id,
      type: rolleTypeStudiested,
    };

    praksiskalenderApi
      .praksisaktivitetIdStudiestedrollePost({
        pathParams: { id: aktivitetsId },
        nyStudiestedbrukerRolleDto: rolle,
      })
      .then(() => {
        addSuccessNotification(
          handleAddSuccessStudiestedLabel(rolleTypeStudiested),
        );
        rollerReload();
      })
      .catch((err: HttpError) => {
        addErrorNotification(err.body);
      });
  };

  const savePraksisstedbrukerModal = (
    praksisstedbruker: PraksisstedbrukerDto,
    praksisstedId: string,
  ) => {
    const rolle: NyPraksisstedbrukerRolleDto = {
      praksisstedbrukerId: praksisstedbruker && praksisstedbruker.id,
      type: PraksisstedRollerDto.Veileder,
      praksisstedId: praksisstedId,
    };

    praksiskalenderApi
      .praksisaktivitetIdPraksisstedrollePost({
        pathParams: { id: aktivitetsId },
        nyPraksisstedbrukerRolleDto: rolle,
      })
      .then(() => {
        addSuccessNotification(labels.addSuccessVeileder);
        rollerReload();
      })
      .catch((err: HttpError) => {
        addErrorNotification(err.body);
      });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortRowsByRolle = useCallback(
    (col: number, a: RolleDto, b: RolleDto, studiested: boolean) => {
      switch (col) {
        case 0:
          return compare(a.etternavn, b.etternavn);
        case 1:
          return compare(
            studiested ? a.type.studiestedrolle! : a.type.praksisstedrolle!,
            studiested ? b.type.studiestedrolle! : b.type.praksisstedrolle!,
          );
        case 2:
          return compare(a.epost, b.epost);
      }
      return 0;
    },
    [compare],
  );

  const sortUhaByActiveCol = useCallback(
    (col: number, ascending: boolean) => {
      setUhaTableRows(
        rollerData
          ?.filter(
            (rolle) =>
              rolle.type.studiestedrolle === StudiestedRollerDto.Underviser ||
              rolle.type.studiestedrolle === StudiestedRollerDto.Koordinator,
          )
          .sort((a, b) => {
            return ascending
              ? sortRowsByRolle(col, b, a, true)
              : sortRowsByRolle(col, a, b, true);
          })
          .map(studiestedbrukerTableRow),
      );
    },
    [rollerData, sortRowsByRolle, studiestedbrukerTableRow],
  );

  const sortPsaByActiveCol = useCallback(
    (col: number, ascending: boolean) => {
      setPsaTableRows(
        rollerData
          ?.filter(
            (rolle) =>
              rolle.type.praksisstedrolle === PraksisstedRollerDto.Veileder ||
              rolle.type.praksisstedrolle === PraksisstedRollerDto.Koordinator,
          )
          .sort((a, b) => {
            return ascending
              ? sortRowsByRolle(col, b, a, false)
              : sortRowsByRolle(col, a, b, false);
          })
          .map(praksisstedbrukerTableRow),
      );
    },
    [rollerData, sortRowsByRolle, praksisstedbrukerTableRow],
  );

  const uhaHeaders = useMemo(() => {
    const canSortIndices = [Header.NAVN, Header.ROLLE, Header.EPOST];

    return headersUha.map(({ title, type }) => (
      <TableCellTh
        align={type === headersUha.length - 1 ? 'right' : undefined}
        key={title}
        colIndex={type}
        activeCol={type === activeUhaCol}
        setActiveCol={setActiveUhaCol}
        sort={sortUhaByActiveCol}
        canSort={canSortIndices.includes(type)}
      >
        {title}
      </TableCellTh>
    ));
  }, [headersUha, activeUhaCol, sortUhaByActiveCol]);

  const psaHeaders = useMemo(() => {
    const canSortIndices = [Header.NAVN, Header.ROLLE, Header.EPOST];

    return headersPsa.map(({ title, type }) => (
      <TableCellTh
        align={type === headersPsa.length - 1 ? 'right' : undefined}
        key={title}
        colIndex={type}
        activeCol={type === activePsaCol}
        setActiveCol={setActivePsaCol}
        sort={sortPsaByActiveCol}
        canSort={canSortIndices.includes(type)}
      >
        {title}
      </TableCellTh>
    ));
  }, [headersPsa, activePsaCol, sortPsaByActiveCol]);

  const onClosePraksisstedbrukerDialog = (
    ok: boolean,
    valgtBruker?: PraksisstedbrukerDto,
  ) => {
    if (ok && valgtBruker && selectedPraksissteder) {
      savePraksisstedbrukerModal(
        valgtBruker as PraksisstedbrukerDto,
        selectedPraksissteder.id,
      );
    }
  };

  return (
    <>
      {rollerLoading && <Spinner />}
      {rollerData && (
        <Table id={'uha-users'}>
          <TableHeader>
            <TableRow top>{uhaHeaders}</TableRow>
          </TableHeader>
          <TableMain>
            {uhaTableRows ||
              rollerData
                .filter(
                  (rolle) =>
                    rolle.type.studiestedrolle ===
                      StudiestedRollerDto.Underviser ||
                    rolle.type.studiestedrolle ===
                      StudiestedRollerDto.Koordinator,
                )
                .map(studiestedbrukerTableRow)}
          </TableMain>
        </Table>
      )}
      <RollelisteStudiestedbrukerDialog
        id="velg-studiebruker"
        rollerData={rollerData}
        title={`${emnekode} - ${emnenavn}`}
        onClose={(ok, valgtBruker, rolleTypeStudiested) => {
          if (ok && bruker && rolleTypeStudiested) {
            saveStudiestedbrukerModal(
              valgtBruker as StudiestedbrukerDto,
              rolleTypeStudiested,
            );
          }
        }}
        titleValue={sted}
      />
      {rollerLoading && <Spinner />}
      {rollerData && (
        <Table id={'psa-users'}>
          <TableHeader>
            <TableRow top>{psaHeaders}</TableRow>
          </TableHeader>
          <TableMain>
            {psaTableRows ||
              rollerData
                .filter(
                  (rolle) =>
                    rolle.type.praksisstedrolle ===
                      PraksisstedRollerDto.Veileder ||
                    rolle.type.praksisstedrolle ===
                      PraksisstedRollerDto.Koordinator,
                )
                .map(praksisstedbrukerTableRow)}
          </TableMain>
        </Table>
      )}
      {canWrite && (
        <RollelistePraksisstedbrukerDialog
          id="velg-praksisbruker"
          defaultPraksissted={selectedPraksissteder}
          rollerData={rollerData}
          title={`${emnekode} - ${emnenavn}`}
          onClose={onClosePraksisstedbrukerDialog}
          onSelectedPraksissted={setSelectedPraksissteder}
        />
      )}
    </>
  );
};

export default RolleListe;
